import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useToast } from '@/hooks/use-toast';
import { useClients, useCreateClient, ClientCapReachedError } from '@/services/clients';
import { useCreateProject, useProjects, ProjectCapReachedError } from '@/services/projects';
import { useOrganization } from '@/services/organizations';
import { useAddMilestone } from '@/services/milestones';
import { useCreativeRole } from '@/hooks/useCreativeRole';
import { useSubscription } from '@/hooks/useSubscription';
import { UpgradeNudgeCard } from '@/components/shared/UpgradeNudgeCard';
import { Plus, Sparkles } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { SuccessConfetti } from '@/components/shared/SuccessConfetti';
import { useNavigate } from 'react-router-dom';
import { NovaSpinner } from '@/components/nova';
import { debug } from '@/lib/debug';

interface CreateProjectModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * CLIENT-1 — Radix's Select uses '' internally for "nothing selected",
 * so the inline "Add client" row needs a sentinel value of its own. It
 * never reaches state: `onValueChange` intercepts it.
 */
const ADD_CLIENT = '__add_client__';

const campaignTypes = [
  { value: 'product', label: 'Product' },
  { value: 'brand', label: 'Brand' },
  { value: 'story', label: 'Story' },
  { value: 'launch', label: 'Launch' },
  { value: 'event', label: 'Event' },
  { value: 'documentary', label: 'Documentary' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'music_video', label: 'Music Video' },
  { value: 'other', label: 'Other' },
];

const CreateProjectModal = ({ open, onOpenChange }: CreateProjectModalProps) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { data: clients } = useClients();
  const createClient = useCreateClient();
  const { data: organization } = useOrganization();
  const { data: existingProjects = [] } = useProjects();
  const createProject = useCreateProject();
  const addMilestone = useAddMilestone();
  // ONB-3 — role bundle drives the first-project pre-fills. Only
  // fires when the studio has ZERO projects; every subsequent
  // create dialog opens blank so we don't fight the studio's own
  // naming instincts.
  const { role, personalization } = useCreativeRole();
  const isFirstProject = existingProjects.length === 0;

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [clientId, setClientId] = useState<string>('');
  const [campaignType, setCampaignType] = useState<string>('');
  const [budget, setBudget] = useState('');
  const [dueDate, setDueDate] = useState('');
  /// Shoot day — different from due date. Powers the weather chip
  /// on the project workspace overview tab once set.
  const [scheduledDate, setScheduledDate] = useState('');
  /// Free-text location ("Mountain Lodge, Aspen CO"). Geocoded
  /// client-side via Open-Meteo when the weather chip mounts.
  const [location, setLocation] = useState('');
  const [portalEnabled, setPortalEnabled] = useState(true);
  const [showConfetti, setShowConfetti] = useState(false);
  // PLANS-1B — inline cap-reached state so the create dialog can swap
  // the destructive toast for an UpgradeNudgeCard when Starter hits
  // its 5-active-projects cap.
  const [capReached, setCapReached] = useState(false);
  // CLIENT-1 — "in dropdown + add client option." Making a project for
  // someone who isn't in the roster yet meant abandoning this dialog,
  // going to Clients, creating them, and starting over — losing
  // everything typed so far. The form stays inline rather than opening
  // a second dialog over this one: a modal on a modal is where the
  // Select's portal, the two focus traps and Escape all start
  // disagreeing, and this is three fields.
  const [addingClient, setAddingClient] = useState(false);
  const [newClientName, setNewClientName] = useState('');
  const [newClientEmail, setNewClientEmail] = useState('');
  const [newClientPhone, setNewClientPhone] = useState('');
  const [savingClient, setSavingClient] = useState(false);
  const [clientCapReached, setClientCapReached] = useState(false);
  const { plan } = useSubscription();

  const resetNewClient = () => {
    setAddingClient(false);
    setNewClientName('');
    setNewClientEmail('');
    setNewClientPhone('');
    setClientCapReached(false);
  };

  const handleCreateClient = async () => {
    const name = newClientName.trim();
    if (!name) {
      toast({ title: 'Give the client a name', variant: 'destructive' });
      return;
    }
    if (!organization?.id) {
      toast({
        title: 'Your studio isn’t set up yet',
        description: 'Finish your studio setup and the client will save.',
        variant: 'destructive',
      });
      return;
    }
    setSavingClient(true);
    try {
      const created = await createClient.mutateAsync({
        name,
        email: newClientEmail.trim() || null,
        phone: newClientPhone.trim() || null,
        org_id: organization.id,
      });
      // Select them immediately — creating a client from inside the
      // project form only makes sense if it also answers the question
      // that sent them here.
      setClientId(created.id);
      resetNewClient();
      toast({ title: 'Client added', description: `${created.name} is on this project.` });
    } catch (error) {
      // The mutation deliberately does NOT toast for the cap so the
      // caller can render the nudge instead (see useCreateClient).
      if (error instanceof ClientCapReachedError) {
        setClientCapReached(true);
        return;
      }
      toast({
        title: 'Error creating client',
        description: debug.parseSupabaseError(error),
        variant: 'destructive',
      });
    } finally {
      setSavingClient(false);
    }
  };

  // ONB-3 — pre-fill the name + campaign type the first time this
  // dialog opens for a fresh studio. `useEffect` on `open` +
  // `isFirstProject` so the prefill applies even if the studio
  // opened the dialog before their role/projects finished loading,
  // but never re-populates over user edits (only pre-fills when the
  // fields are still empty). Once they have a project, subsequent
  // opens stay blank.
  useEffect(() => {
    if (!open || !isFirstProject) return;
    setName((prev) => prev || personalization.suggestedFirstProject);
    setCampaignType((prev) => prev || personalization.firstProjectCampaignType);
  }, [
    open,
    isFirstProject,
    personalization.suggestedFirstProject,
    personalization.firstProjectCampaignType,
  ]);

  const handleCreate = async () => {
    if (!name || !organization) return;
    setCapReached(false);

    try {
      const project = await createProject.mutateAsync({
        name,
        description: description || null,
        client_id: clientId || null,
        campaign_type: campaignType as any || null,
        budget: budget ? parseFloat(budget) : null,
        due_date: dueDate || null,
        scheduled_date: scheduledDate || null,
        location: location.trim() || null,
        portal_enabled: portalEnabled,
        org_id: organization.id,
        status: 'discovery',
      } as any);

      // ONB-3 — seed craft-specific milestones on the studio's
      // very first project. Photographer gets shoot + edit + deliver;
      // videographer gets pre-prod + shoot + post-prod + delivery.
      // Fire-and-forget: a milestone insert failure shouldn't fail
      // the whole create flow.
      if (isFirstProject && personalization.firstProjectMilestones.length > 0) {
        try {
          for (const milestoneName of personalization.firstProjectMilestones) {
            await addMilestone.mutateAsync({
              projectId: project.id,
              name: milestoneName,
            });
          }
        } catch (err) {
          // Non-fatal — the project exists, milestones can be
          // added by hand from the project workspace.
          console.warn('[create-project] seed milestones failed', err);
        }
      }

      // BUGFIX — close and go, now, rather than waiting on an
      // animation.
      //
      // This used to set the confetti running and leave the dialog
      // open until the confetti said it had finished, which was the
      // ONLY path that closed it. Two things were wrong with that:
      // the confetti's completion callback never fired at all (see
      // SuccessConfetti), so the dialog never closed; and even
      // working, it made a studio watch a saved project sit behind an
      // open form for three and a half seconds. A save that looks
      // like it failed is a save the studio does twice.
      //
      // The confetti is a SIBLING of the dialog, so it plays over the
      // project page it just landed on.
      setShowConfetti(true);
      toast({ title: 'Project created successfully' });
      onOpenChange(false);
      resetForm();
      navigate(`/studio/${project.id}`);
    } catch (error) {
      // PLANS-1B — Starter cap reached (5 active projects). Render
      // the upgrade nudge inline inside the dialog instead of a toast.
      if (error instanceof ProjectCapReachedError) {
        setCapReached(true);
        return;
      }
      // HOTFIX-3 — surface the real failure instead of a bare title.
      // Supabase's PostgrestError is NOT an `instanceof Error`, so it
      // must go through debug.parseSupabaseError (which also maps RLS
      // 42501 to a human "Permission denied" message) rather than
      // `error.message` on a typed-as-Error catch.
      toast({
        title: 'Error creating project',
        description: debug.parseSupabaseError(error),
        variant: 'destructive',
      });
    }
  };

  // Just the teardown now — closing and navigating happen the moment
  // the project exists.
  const handleConfettiComplete = () => {
    setShowConfetti(false);
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setClientId('');
    setCampaignType('');
    setBudget('');
    setDueDate('');
    setScheduledDate('');
    setLocation('');
    setPortalEnabled(true);
    resetNewClient();
  };

  return (
    <>
      <SuccessConfetti trigger={showConfetti} onComplete={handleConfettiComplete} />
      <Dialog
        open={open}
        onOpenChange={(next) => {
          onOpenChange(next);
          if (!next) {
            setCapReached(false);
            // CLIENT-1 — a half-typed client must not be sitting there
            // the next time this dialog opens.
            resetNewClient();
          }
        }}
      >
        <DialogContent className="max-w-lg card-glass">
        <DialogHeader>
          <DialogTitle>Create New Project</DialogTitle>
          <DialogDescription>
            Set up a new production project for your client
          </DialogDescription>
          {/* ONB-3 — surface the fact that Nova has pre-shaped this
              first project for the studio's craft. Only renders on
              the very first create; every subsequent open is blank. */}
          {isFirstProject && role !== 'other' && (
            <p className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium text-orbit-orange">
              <OrbitIcon name="sparkles" fallback={Sparkles} className="w-3 h-3" />
              Pre-filled for a {personalization.roleChipLabel.toLowerCase()} —
              rename anything, and Nova will drop {personalization.firstProjectMilestones.length} starter milestones once you create it.
            </p>
          )}
        </DialogHeader>

        <div className="space-y-4 py-4">
          {capReached && (
            <UpgradeNudgeCard
              reason="project_cap"
              currentPlan={plan}
              ctaHref="/settings?tab=billing"
            />
          )}
          {/* Project Name */}
          <div className="space-y-2">
            <Label htmlFor="project-name">Project Name *</Label>
            <Input
              id="project-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Summer Campaign 2024"
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What are you making?"
              rows={3}
            />
          </div>

          {/* Client Selection */}
          <div className="space-y-2">
            <Label>Client</Label>
            <Select
              value={clientId}
              onValueChange={(value) => {
                if (value === ADD_CLIENT) {
                  // Sentinel — open the inline form, leave the
                  // selection alone so cancelling loses nothing.
                  setAddingClient(true);
                  return;
                }
                setClientId(value);
              }}
            >
              <SelectTrigger className="bg-background border-border">
                <SelectValue placeholder="Select a client" />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border z-[9999]">
                {/* CLIENT-1 — pinned to the top, because the studios who
                    need it are the ones whose list is short or empty. */}
                <SelectItem value={ADD_CLIENT}>
                  <span className="flex items-center gap-2 text-foreground">
                    <OrbitIcon name="plus" fallback={Plus} className="h-3.5 w-3.5" />
                    Add a client
                  </span>
                </SelectItem>
                {clients && clients.length > 0 && (
                  <div className="my-1 h-px bg-border/60" role="separator" />
                )}
                {clients?.map((client) => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {addingClient && (
              <div className="space-y-3 rounded-xl border border-border/60 bg-muted/20 p-3">
                {clientCapReached ? (
                  <UpgradeNudgeCard
                    reason="client_cap"
                    currentPlan={plan}
                    ctaHref="/settings?tab=billing"
                  />
                ) : (
                  <>
                    <div className="space-y-1.5">
                      <Label htmlFor="new-client-name" className="text-xs">
                        Client name *
                      </Label>
                      <Input
                        id="new-client-name"
                        autoFocus
                        value={newClientName}
                        onChange={(e) => setNewClientName(e.target.value)}
                        onKeyDown={(e) => {
                          // Enter saves the client; without this it
                          // submits the project form around it.
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void handleCreateClient();
                          }
                        }}
                        placeholder="Ashlyn & Hayden"
                        className="bg-background border-border"
                        disabled={savingClient}
                      />
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label htmlFor="new-client-email" className="text-xs">
                          Email
                        </Label>
                        <Input
                          id="new-client-email"
                          type="email"
                          value={newClientEmail}
                          onChange={(e) => setNewClientEmail(e.target.value)}
                          placeholder="Optional"
                          className="bg-background border-border"
                          disabled={savingClient}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="new-client-phone" className="text-xs">
                          Phone
                        </Label>
                        <Input
                          id="new-client-phone"
                          type="tel"
                          value={newClientPhone}
                          onChange={(e) => setNewClientPhone(e.target.value)}
                          placeholder="Optional"
                          className="bg-background border-border"
                          disabled={savingClient}
                        />
                      </div>
                    </div>
                  </>
                )}
                <div className="flex items-center justify-end gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={resetNewClient} disabled={savingClient}>
                    Cancel
                  </Button>
                  {!clientCapReached && (
                    <Button
                      type="button"
                      size="sm"
                      onClick={() => void handleCreateClient()}
                      disabled={savingClient || !newClientName.trim()}
                    >
                      {savingClient ? <NovaSpinner size={14} /> : 'Save client'}
                    </Button>
                  )}
                </div>
              </div>
            )}

            {!addingClient && (!clients || clients.length === 0) && (
              <p className="text-xs text-muted-foreground">
                No clients yet — choose “Add a client” above, or add one in the Clients page.
              </p>
            )}
          </div>

          {/* Campaign Type */}
          <div className="space-y-2">
            <Label>Campaign Type</Label>
            <Select value={campaignType} onValueChange={setCampaignType}>
              <SelectTrigger className="bg-background border-border">
                <SelectValue placeholder="Select campaign type" />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border z-[9999]">
                {campaignTypes.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {type.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Budget & Due Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="budget">Budget ($)</Label>
              <Input
                id="budget"
                type="number"
                value={budget}
                onChange={(e) => setBudget(e.target.value)}
                placeholder="10000"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="due-date">Due Date</Label>
              <Input
                id="due-date"
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
              />
            </div>
          </div>

          {/* Shoot Date & Location — power the weather chip + Today's
              "next up" countdown. Optional, but having both is what
              makes Orbit feel ambient and predictive. */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="scheduled-date">Shoot date</Label>
              <Input
                id="scheduled-date"
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Aspen, CO"
              />
            </div>
          </div>

          {/* Portal Enabled */}
          <div className="flex items-center justify-between p-4 rounded-lg border border-border">
            <div>
              <p className="font-medium text-foreground">Enable Client Portal</p>
              <p className="text-sm text-muted-foreground">
                Allow clients to view project progress and deliverables
              </p>
            </div>
            <Switch
              checked={portalEnabled}
              onCheckedChange={setPortalEnabled}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={!name || createProject.isPending}>
            {createProject.isPending ? (
              <>
                <NovaSpinner size={16} className="mr-2" />
                Creating...
              </>
            ) : (
              <>
                <OrbitIcon name="plus" fallback={Plus} className="w-4 h-4 mr-2" />
                Create Project
              </>
            )}
          </Button>
        </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreateProjectModal;
